import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const DowntownVancouver = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName} ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName} ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.downtown-vancouver:About Downtown Vancouver Paragraph">
          Living in Downtown{' '}
          <LinkText href="/rental-listings/city/vancouver">Vancouver</LinkText>,{' '}
          <LinkText href="/rental-listings/british-columbia">BC</LinkText>{' '}
          offers a unique and exciting experience with its bustling atmosphere,
          diverse communities, and endless entertainment options. Convenience is
          a major advantage, as everything you need is within walking distance,
          with excellent public transportation options available. The downtown
          area boasts world-class shopping, dining, and entertainment, including
          the Pacific Centre and a wide variety of restaurants. Each
          neighborhood within Downtown Vancouver has its own distinctive
          character, from the historic charm of Gastown to the cosmopolitan vibe
          of{' '}
          <LinkText href="https://liv.rent/blog/neighbourhoods/neighborhood-guide-living-in-yaletown/">
            Yaletown
          </LinkText>
          . Despite being a bustling city center, Downtown Vancouver is
          surrounded by natural beauty, with opportunities for outdoor
          recreation such as strolling along the seawall or exploring Stanley
          Park’s lush greenery. Outdoor enthusiasts will also appreciate the
          proximity to hiking trails, beaches, and ski resorts.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$2,800 - $3,500',
          bed2: '$3,700 - $4,900',
          bed3: '$5,000 - $6,900'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.downtown-vancouver:Apartment Hunting Downtown Vancouver Paragraph 1">
            Finding an apartment for rent in Downtown Vancouver, BC requires
            careful planning and consideration. Start by determining your
            criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant downtown lifestyle by exploring your
            new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.downtown-vancouver:Apartment Hunting Downtown Vancouver Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-vancouver:Apartments for rent in Downtown Vancouver Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Downtown Vancouver
              </LinkText>{' '}
              are a popular choice for individuals and young professionals
              looking for a convenient and low-maintenance living option. With a
              variety of sizes and styles available, residents can find the
              perfect space to suit their needs. From sleek and modern high-rise
              buildings to charming heritage apartments, there is something for
              everyone in this bustling neighborhood.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-vancouver:Houses for rent in Downtown Vancouver Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Downtown Vancouver
              </LinkText>{' '}
              present a distinctive living experience for families and those who
              appreciate privacy and outdoor space. With a blend of historic
              homes and contemporary designs, residents can relish the charm and
              character of the neighborhood while still maintaining proximity to
              amenities and attractions.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-vancouver:Townhouses for rent in Downtown Vancouver Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Downtown Vancouver
              </LinkText>{' '}
              offer a harmonious blend of communal living and personal privacy.
              These multi-level homes with small outdoor spaces are perfect for
              individuals seeking extra room to relax. Townhouse living provides
              the perfect balance, enabling residents to enjoy the advantages of
              a tight-knit community while also having their own secluded space.
              Whether you’re in search of rental townhouses in Downtown
              Vancouver or considering purchasing one, these properties present
              a distinct opportunity to immerse yourself in the bustling city
              while enjoying a cozy and expansive abode.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-vancouver:Basements for rent in Downtown Vancouver Paragraph">
              <LinkText href={links.basement}>
                Basements for rent in Downtown Vancouver
              </LinkText>
              , are a cost-effective option for singles or students looking for
              option for singles or students looking for affordable housing.
              These basements for rent often provide a separate entrance and
              basic amenities, making them a practical choice for those on a
              budget. While basements for rent in Downtown Vancouver may not
              offer the same level of natural light as other options, they do
              provide a cozy and private living space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-vancouver:Condos for rent Downtown Vancouver Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Downtown Vancouver
              </LinkText>{' '}
              are a popular choice for those who value convenience and a sense
              of community. With amenities such as fitness centers, rooftop
              gardens, and concierge services, condos for rent in Downtown
              Vancouver offer a maintenance-free lifestyle. Residents can enjoy
              the vibrant energy of the neighborhood while having all the
              comforts of home.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-vancouver:Rooms for rent in Downtown Vancouver Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Downtown Vancouver
              </LinkText>{' '}
              provide an affordable and flexible housing option. Whether you’re
              a student looking for a shared living arrangement or someone who
              enjoys the social aspect of communal living, renting a room allows
              you to live in the heart of the city while saving on costs. With a
              friendly atmosphere and the opportunity to meet new people,
              renting a room can be a great way to experience Downtown Vancouver
              on a budget.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-vancouver:Short term rentals in Downtown Vancouver Paragraph">
              Downtown Vancouver, with its diverse employment opportunities and
              academic institutions, is popular for{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText>,
              especially during summer and the start of school semesters. This
              demand is driven by tourists, business travelers, and students,{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                making short-term rentals a flexible option
              </LinkText>{' '}
              for a variety of needs. The convenience and adaptability of these
              rentals make them a key consideration for those seeking
              accommodation in this vibrant urban area.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.downtown-vancouver:Exploring Downtown Vancouver Paragraph">
          Downtown Vancouver is a vibrant tapestry of neighborhoods, each with
          its own charm and offerings.{' '}
          <LinkText href="https://www.destinationvancouver.com/vancouver/neighbourhoods/gastown/">
            Gastown
          </LinkText>
          , with its historic allure and artistic vibe, features cobblestone
          streets, Victorian architecture, and trendy establishments, making it
          a popular choice for art enthusiasts and trendsetters. The{' '}
          <LinkText href="https://liv.rent/blog/neighbourhoods/neighbourhood-guide-west-end-vancouver/">
            West End
          </LinkText>
          , located in the heart of downtown, is a diverse and lively
          neighborhood known for its high-rise residential towers, picturesque
          parks, and proximity to{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/stanley-park.aspx">
            Stanley Park
          </LinkText>
          . Meanwhile,{' '}
          <LinkText href="https://liv.rent/blog/neighbourhoods/neighborhood-guide-living-in-yaletown/">
            Yaletown
          </LinkText>{' '}
          offers an upscale and trendy atmosphere, with high-tech companies,
          chic boutiques, and fashionable dining options, attracting young
          professionals and families seeking a luxurious urban lifestyle.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.downtown-vancouver:Things To Do Downtown Vancouver Paragraph">
          Downtown Vancouver offers a plethora of activities and attractions
          that cater to a wide range of interests. Nature lovers can explore
          Stanley Park, a sprawling urban oasis with scenic trails, a seawall
          for biking and walking, and the{' '}
          <LinkText href="https://www.vanaqua.org/">
            Vancouver Aquarium
          </LinkText>
          . For art enthusiasts, the{' '}
          <LinkText href="https://www.vanartgallery.bc.ca/">
            Vancouver Art Gallery
          </LinkText>{' '}
          showcases a diverse collection of contemporary and historical
          artworks. Food lovers can indulge in a culinary adventure in the
          vibrant food scene of{' '}
          <LinkText href="https://granvilleisland.com/">
            Granville Island
          </LinkText>
          , where they can sample local produce, seafood, and artisanal goods at
          the Public Market. Shopping enthusiasts can explore{' '}
          <LinkText href="https://www.destinationvancouver.com/vancouver/neighbourhoods/robson-street/">
            Robson Street
          </LinkText>
          , known for its high-end boutiques and international brands.
          Additionally, visitors can take a stroll along the picturesque
          waterfront of{' '}
          <LinkText href="https://liv.rent/blog/neighbourhoods/neighborhood-guide-coal-harbour/">
            Coal Harbour
          </LinkText>{' '}
          or enjoy a scenic ride on the Aquabus to Granville Island. With its
          rich cultural offerings, stunning natural beauty, and diverse range of
          activities, Downtown Vancouver is a must-visit destination for both
          locals and tourists alike.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.downtown-vancouver:Restaurants Downtown Vancouver Paragraph">
        <Paragraph>
          Downtown Vancouver is renowned for its vibrant food scene, offering a
          plethora of dining options to satisfy every palate. Here are the top
          five restaurants that should not be missed when exploring the culinary
          delights of downtown Vancouver:
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="https://hawksworthrestaurant.com/">
            Hawksworth Restaurant
          </LinkText>{' '}
          - Located in the heart of the city, Hawksworth Restaurant is an
          award-winning establishment known for its sophisticated and
          contemporary Canadian cuisine. Led by renowned chef David Hawksworth,
          this fine dining restaurant offers an exquisite menu featuring
          seasonal ingredients sourced from local farms and purveyors.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://mikurestaurant.com/">Miku Vancouver</LinkText>{' '}
          - Miku Vancouver is a must-visit for sushi lovers. This upscale
          Japanese restaurant is famous for its innovative Aburi-style sushi,
          where the seafood is lightly flame-seared to enhance its flavor. With
          its elegant ambiance and breathtaking views of the waterfront, Miku
          Vancouver offers a memorable dining experience.
        </Paragraph>
        <Paragraph>
          3) <LinkText href="https://www.chambar.com/">Chambar</LinkText> - For
          a taste of Belgian-inspired cuisine, Chambar is the place to be. This
          trendy restaurant offers a menu that combines traditional Belgian
          flavors with local ingredients. From their famous moules frites to
          their delectable waffles, Chambar promises a unique and flavorful
          dining experience.
        </Paragraph>
        <Paragraph>
          4){' '}
          <LinkText href="https://www.bluewatercafe.net/">
            Blue Water Cafe
          </LinkText>{' '}
          - Seafood enthusiasts will be delighted by the offerings at Blue Water
          Cafe. Located in the historic Yaletown neighborhood, this upscale
          restaurant is known for its fresh and sustainable seafood dishes. With
          an extensive selection of oysters, sushi, and seafood platters, Blue
          Water Cafe is a favorite among locals and visitors alike.
        </Paragraph>
        <Paragraph>
          5){' '}
          <LinkText href="https://hawknightingale.com/">Nightingale</LinkText> -
          If you’re looking for a more casual dining experience, Nightingale is
          the perfect choice. This contemporary Canadian restaurant, helmed by
          chef David Hawksworth, offers a menu focused on seasonal ingredients
          and wood-fired cooking. With its stylish decor and lively atmosphere,
          Nightingale is a popular spot for brunch, lunch, and dinner.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.downtown-vancouver:Transportation Downtown Vancouver Paragraph">
          Transportation options in downtown Vancouver, BC are plentiful and
          convenient. The city offers a comprehensive{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses,{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , and{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/seabus">
            SeaBus
          </LinkText>
          , which provide easy access to various destinations within the
          downtown area. For those who prefer cycling, Vancouver offers a
          network of bike lanes and rental services, making it a bike-friendly
          city. Additionally, downtown Vancouver is easily walkable, with many
          attractions and amenities located within close proximity. For those
          who prefer driving, there are numerous parking options available,
          including street parking and parking garages. Overall, downtown
          Vancouver provides a range of transportation options to suit every
          traveler’s needs.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.downtown-vancouver:Where To Stay Downtown Vancouver Paragraph">
          When it comes to accommodation options in Downtown Vancouver, BC,
          there is something to suit every traveler’s preferences and budget.
          The neighborhood offers a wide range of hotels, from luxury
          establishments to budget-friendly options. For those seeking a
          luxurious experience, there are renowned hotels such as the{' '}
          <LinkText href="https://www.fairmont.com/pacific-rim-vancouver/">
            Fairmont Pacific Rim
          </LinkText>{' '}
          and the{' '}
          <LinkText href="https://www.shangri-la.com/">
            Shangri-La Hotel
          </LinkText>
          , which boast stunning views, impeccable service, and top-notch
          amenities. On the other hand, budget-conscious travelers can find
          comfortable and affordable accommodations at hotels like the{' '}
          <LinkText href="https://www.wyndhamhotels.com/days-inn/vancouver-british-columbia/days-inn-vancouver-downtown/overview">
            Days Inn by Wyndham Vancouver Downtown
          </LinkText>
          . Additionally, Downtown Vancouver offers various options for
          short-term rentals, including{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText> - a popular
          platform that allows homeowners to rent out their properties to
          travelers on a short-term basis. This can range from a spare room in
          someone’s home to an entire apartment or house. With a wide range of
          options available, from cozy apartments to luxurious penthouses,
          Downtown Vancouver is the perfect place to stay while exploring the
          vibrant city.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default DowntownVancouver
